import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import useUpdate from "../../api/useUpdate";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import { getUniqueCurrencies } from "../../utils/currencyOptions";

const ExchangeRateEdit = ({ open, onClose, editData, token }) => {
  const { t } = useTranslation();
  const { handleUpdate, loading, isSuccess } = useUpdate(
    "shop/exchange-rates",
    token
  );
  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(
    ["currencies", "exchange-rate-edit"],
    "shop/currencies/?page_size=all",
    token
  );

  const currencies = getUniqueCurrencies(
    Array.isArray(currenciesData) ? currenciesData : currenciesData?.results || []
  );

  const [fromCurrency, setFromCurrency] = useState("");
  const [toCurrency, setToCurrency] = useState("");
  const [rate, setRate] = useState("");
  const [rateDate, setRateDate] = useState("");

  useEffect(() => {
    if (editData) {
      setFromCurrency(
        String(editData.from_currency?.id || editData.from_currency || "").trim()
      );
      setToCurrency(
        String(editData.to_currency?.id || editData.to_currency || "").trim()
      );
      setRate(editData.rate ? String(editData.rate) : "");
      setRateDate(editData.rate_date || "");
    }
  }, [editData]);

  const handleSubmit = () => {
    const payload = {
      from_currency: fromCurrency,
      to_currency: toCurrency,
      rate,
      rate_date: rateDate,
    };
    handleUpdate(editData.id, payload);
  };

  useEffect(() => {
    if (isSuccess) {
      onClose();
    }
  }, [isSuccess, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("exchangeRate.editExchangeRate")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <TextField
            select
            margin="dense"
            label={t("exchangeRate.fromCurrency")}
            fullWidth
            required
            value={fromCurrency}
            onChange={(e) => setFromCurrency(e.target.value)}
          >
            {currencyLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              currencies.map((currency) => (
                <MenuItem key={currency.id} value={String(currency.id)}>
                  {currency.title}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            select
            margin="dense"
            label={t("exchangeRate.toCurrency")}
            fullWidth
            required
            value={toCurrency}
            onChange={(e) => setToCurrency(e.target.value)}
          >
            {currencyLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              currencies.map((currency) => (
                <MenuItem key={currency.id} value={String(currency.id)}>
                  {currency.title}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            margin="dense"
            label={t("exchangeRate.rate")}
            type="number"
            inputProps={{ step: "0.000001", min: "0" }}
            fullWidth
            required
            value={rate}
            onChange={(e) => setRate(e.target.value)}
          />

          <TextField
            margin="dense"
            label={t("exchangeRate.rateDate")}
            type="date"
            fullWidth
            required
            value={rateDate}
            onChange={(e) => setRateDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("exchangeRate.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading ||
            !fromCurrency ||
            !toCurrency ||
            !rate ||
            !rateDate
          }
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("exchangeRate.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExchangeRateEdit;
